import { doc, updateDoc } from 'firebase/firestore';
import { useState } from 'react';
import { firestore } from '../services/firebase';
import { shareTenderWithPartner } from '../services/sharingService';
import { getPublicKey, linkPartnerRequest } from '../services/userService';
import type { User } from '../types/auth';
import type { RecommendedPartner, Tender } from '../types/tender';

const EMPTY_ARRAY: never[] = [];

export interface UseTenderPartnersProps {
  tender: Tender | null;
  account: (User & { clientId?: string }) | null;
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

export const useTenderPartners = ({ tender, account, showSnackbar }: UseTenderPartnersProps) => {
  const [linkPartnerDialog, setLinkPartnerDialog] = useState<{
    open: boolean;
    partner: RecommendedPartner | null;
  }>({ open: false, partner: null });
  const [isLinking, setIsLinking] = useState(false);

  const handleContactPartner = async (partner: RecommendedPartner) => {
    if (!partner.contactEmail) {
      showSnackbar('Partner contact email is not available.', 'error');
      return;
    }
    const subject = encodeURIComponent(
      `BIDLOGIQ Partner Request: ${tender?.title || 'Unknown Tender'}`
    );
    const tenderLinkString = tender?.tenderSourceURL
      ? `\n\nLink to Tender:\n${tender.tenderSourceURL}`
      : '';
    const scope =
      tender?.scope || tender?.summary || tender?.description || 'No scope details available.';
    const body = encodeURIComponent(
      `Hi ${partner.companyName} team,\n\nWe came across your capability profile via the BIDLOGIQ platform and believe you'd be a great fit to partner with us on an upcoming bid.\n\nTender: ${tender?.title || 'Unknown'}\n\nBrief Scope:\n${scope}${tenderLinkString}\n\nWe would love to schedule a brief call to discuss this opportunity and see if you have the capacity to collaborate. If you are interested, please reply to this email or accept the secure partnership request when you receive the notification in your BIDLOGIQ dashboard.\n\nBest regards,\n${account?.displayName}`
    );
    window.location.href = `mailto:${partner.contactEmail}?subject=${subject}&body=${body}`;

    if (partner.userId && tender?.id) {
      try {
        await linkPartnerRequest(tender.id, partner.userId);

        const updatedPartners =
          tender.alignmentAnalysis?.recommendedPartners?.map((p) =>
            p.partnerId === partner.partnerId ? { ...p, linkStatus: 'pending' as const } : p
          ) || EMPTY_ARRAY;

        await updateDoc(doc(firestore, 'tenders', tender.id), {
          'alignmentAnalysis.recommendedPartners': updatedPartners,
        });

        showSnackbar('Partner request notification sent.', 'success');
      } catch (err) {
        const error = err as Error;
        showSnackbar(error.message || 'Failed to send partner request notification.', 'warning');
      }
    }
  };

  const handleLinkPartner = (partner: RecommendedPartner) => {
    setLinkPartnerDialog({ open: true, partner });
  };

  const confirmLinkPartner = async () => {
    const partner = linkPartnerDialog.partner;
    if (!partner) {
      return;
    }

    if (!partner.userId) {
      showSnackbar('Partner User ID is not available. Cannot establish secure link.', 'error');
      setLinkPartnerDialog({ open: false, partner: null });
      return;
    }
    if (!tender?.id) {
      setLinkPartnerDialog({ open: false, partner: null });
      return;
    }

    try {
      setIsLinking(true);
      showSnackbar('Establishing secure connection...', 'info');
      const publicKeyJWK = await getPublicKey(partner.userId);
      const shareResult = await shareTenderWithPartner(tender.id, partner.userId, publicKeyJWK);
      if (shareResult.success) {
        const updatedPartners =
          tender.alignmentAnalysis?.recommendedPartners?.map((p) =>
            p.partnerId === partner.partnerId ? { ...p, linkStatus: 'approved' as const } : p
          ) || EMPTY_ARRAY;

        await updateDoc(doc(firestore, 'tenders', tender.id), {
          'alignmentAnalysis.recommendedPartners': updatedPartners,
        });

        showSnackbar(`Tender successfully linked to ${partner.companyName}.`, 'success');
      } else {
        showSnackbar(shareResult.message || 'Failed to link tender.', 'error');
      }
    } catch (err) {
      const error = err as Error;
      showSnackbar(error.message || 'Error occurred while linking partner.', 'error');
    } finally {
      setLinkPartnerDialog({ open: false, partner: null });
    }
  };

  return {
    linkPartnerDialog,
    setLinkPartnerDialog,
    handleContactPartner,
    handleLinkPartner,
    confirmLinkPartner,
    isLinking,
  };
};
